// src/components/defined/Print/PrintMenuBase.jsx
import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";
import DropdownPrint from "../../defined/Report/DropdownPrint";

const sectionTitleStyle = {
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 8,
  color: theme.colors.text.title,
};

const sectionBlockStyle = {
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: `1px solid ${theme.colors.borders.default}`,
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 8,
};

const labelStyle = {
  fontSize: 13,
  color: theme.colors.text.body,
};

const selectStyle = {
  flex: 1,
  height: 32,
  borderRadius: 4,
  border: `1px solid ${theme.colors.borders.default}`,
  padding: "4px 8px",
  fontSize: 13,
  boxSizing: "border-box",
};

const radioGroupStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 16,
};

const buttonPrimaryStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 14px",
  borderRadius: 4,
  border: "none",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  background: theme.buttons.blue.background,   // correct
  color: theme.buttons.blue.color,             // correct
};


const thumbBoxStyle = {
  width: 120,
  height: 80,
  borderRadius: 4,
  border: `1px solid ${theme.colors.borders.default}`,
  background: theme.colors.background.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  color: theme.colors.text.muted,
};

/**
 * PrintMenuBase
 * ---------------------------------------------------------------------------
 * Menu d’impression générique et réutilisable.
 *
 * PROPS :
 *  - items           : tableau d’objets à imprimer (risques, incidents…)
 *  - getItemId       : function(item) -> id
 *  - getItemLabel    : function(item) -> label
 *  - templates       : [{ value, label }]
 *  - defaultTemplate : value du template par défaut
 *  - onGenerate      : function(options)
 *
 * options passées à onGenerate :
 *  {
 *    selectedIds,
 *    onlyActive,
 *    format,         // "pdf" | "docx"
 *    template,       // value
 *    orientation,    // "portrait" | "landscape"
 *    fontSize,       // "small" | "medium" | "large"
 *    includeToc,     // boolean
 *  }
 * ---------------------------------------------------------------------------
 */

const PrintMenuBase = ({
  items = [],
  getItemId = (item) => item.id,
  getItemLabel = (item) => item.title || item.label || `#${item.id}`,
  isActive = (item) => item.statut !== "clos" && item.statut !== "closed",
  templates = [],
  defaultTemplate = null,
  onGenerate = () => {},
}) => {
  // ---------------------------------------------------------------------------
  // Options dérivées
  // ---------------------------------------------------------------------------
  const itemOptions = useMemo(
    () =>
      items.map((item) => ({
        value: String(getItemId(item)),
        label: getItemLabel(item),
      })),
    [items, getItemId, getItemLabel]
  );

  const allIds = useMemo(
    () => items.map((item) => String(getItemId(item))),
    [items, getItemId]
  );

  const activeIds = useMemo(
    () =>
      items
        .filter((item) => isActive(item))
        .map((item) => String(getItemId(item))),
    [items, isActive, getItemId]
  );

  // ---------------------------------------------------------------------------
  // États locaux
  // ---------------------------------------------------------------------------
  const [selectedIds, setSelectedIds] = useState(allIds);
  const [selectAll, setSelectAll] = useState(true);
  const [onlyActive, setOnlyActive] = useState(false);

  const [format, setFormat] = useState("pdf"); // "pdf" | "docx"
  const [selectedTemplate, setSelectedTemplate] = useState(
    defaultTemplate || (templates[0]?.value ?? null)
  );
  const [orientation, setOrientation] = useState("portrait"); // "portrait" | "landscape"
  const [fontSize, setFontSize] = useState("medium"); // "small" | "medium" | "large"
  const [includeToc, setIncludeToc] = useState(true);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedIds(onlyActive ? activeIds : allIds);
    }
  };

  const handleOnlyActiveChange = (checked) => {
    setOnlyActive(checked);
    if (selectAll) {
      setSelectedIds(checked ? activeIds : allIds);
    }
  };

  const handleSelectedValuesChange = (values) => {
    setSelectedIds(values.map(String));
    setSelectAll(values.length === (onlyActive ? activeIds.length : allIds.length));
  };

  const handleGenerate = () => {
    onGenerate({
      selectedIds,
      onlyActive,
      format,
      template: selectedTemplate,
      orientation,
      fontSize,
      includeToc: format === "docx" ? includeToc : false,
    });
  };

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------
  return (
    <div style={{ width: "100%", boxSizing: "border-box" }}>
      {/* SECTION 1 — Sélection des risques */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Sélection des éléments</div>

        <div style={{ marginBottom: 8 }}>
          <DropdownPrint
            label="Éléments à inclure"
            options={itemOptions}
            selectedValues={selectedIds}
            onChange={handleSelectedValuesChange}
            width="100%"
            height={32}
          />
        </div>

        <div style={rowStyle}>
          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAllChange(e.target.checked)}
            />
            Tout sélectionner
          </label>

          <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => handleOnlyActiveChange(e.target.checked)}
            />
            Uniquement les éléments actifs
          </label>
        </div>
      </div>

      {/* SECTION 2 — Format du document */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Format du document</div>

        <div style={radioGroupStyle}>
          <label style={labelStyle}>
            <input
              type="radio"
              name="print-format"
              value="pdf"
              checked={format === "pdf"}
              onChange={() => setFormat("pdf")}
              style={{ marginRight: 6 }}
            />
            PDF
          </label>
          <label style={labelStyle}>
            <input
              type="radio"
              name="print-format"
              value="docx"
              checked={format === "docx"}
              onChange={() => setFormat("docx")}
              style={{ marginRight: 6 }}
            />
            Word (.docx)
          </label>
        </div>
      </div>

      {/* SECTION 3 — Template du document */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Template du document</div>

        <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Template</div>
            <select
              style={selectStyle}
              value={selectedTemplate || ""}
              onChange={(e) => setSelectedTemplate(e.target.value || null)}
            >
              {templates.length === 0 && (
                <option value="">Aucun template disponible</option>
              )}
              {templates.map((tpl) => (
                <option key={tpl.value} value={tpl.value}>
                  {tpl.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Aperçu</div>
            <div style={thumbBoxStyle}>
              {selectedTemplate ? "Aperçu du template" : "Aucun template"}
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 4 — Mise en page */}
      <div style={sectionBlockStyle}>
        <div style={sectionTitleStyle}>Mise en page</div>

        <div style={{ display: "flex", flexDirection: "row", gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Orientation</div>
            <select
              style={selectStyle}
              value={orientation}
              onChange={(e) => setOrientation(e.target.value)}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Paysage</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ ...labelStyle, marginBottom: 4 }}>Taille de police</div>
            <select
              style={selectStyle}
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
            >
              <option value="small">Petit</option>
              <option value="medium">Moyen</option>
              <option value="large">Grand</option>
            </select>
          </div>
        </div>

        {format === "docx" && (
          <div style={rowStyle}>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={includeToc}
                onChange={(e) => setIncludeToc(e.target.checked)}
              />
              Inclure table des matières
            </label>
          </div>
        )}
      </div>

      {/* SECTION 5 — Action */}
      <div style={{ textAlign: "right" }}>
        <button type="button" style={buttonPrimaryStyle} onClick={handleGenerate}>
          Générer le document
        </button>
      </div>
    </div>
  );
};

export default PrintMenuBase;
